import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SettingDefDialog from './settingDef';
import SettingDefSectionDialog from './settingDefSection';

type Props = {
  settingDefs: any[];
  maxHeight?: number;
  onAddSettingDef: (sectionName: string, settingDef: any) => void;
  onUpdateSettingDef: (sectionName: string, settingDef: any) => void;
  onDeleteSettingDef: (sectionName: string, settingDef: any) => void;
  onAddNewSection: (sectionName: string) => void;
  onUpdateSectionName: (oldSectionName: string, newSectionName: string) => void;
  onDeleteSection: (sectionName: string) => void;
};

const SettingDefs = ({
  settingDefs = [],
  onAddSettingDef,
  onUpdateSettingDef,
  onDeleteSettingDef,
  onAddNewSection,
  onUpdateSectionName,
  onDeleteSection,
}: Props) => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openAddSectionDialog, setOpenAddSectionDialog] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const [settingDef, setSettingDef] = useState(null);
  const [settingNames, setSettingNames] = useState<any[]>([]);

  useEffect(() => {
    const names: any[] = [];
    for (const section of settingDefs) {
      names.push(...(section.items || []).map((sd: any) => ({ name: sd.name, type: sd.type })));
    }
    setSettingNames(names);
  }, [settingDefs]);

  const showAddDialog = (sectionName: string) => {
    setSectionName(sectionName);
    setSettingDef(null);
    setOpenAddDialog(true);
  };

  const showEditDialog = (sectionName: string, settingDef: any) => {
    setSectionName(sectionName);
    setSettingDef(settingDef);
    setOpenAddDialog(true);
  };

  const showAddSectionDialog = () => {
    setSectionName('');
    setSettingDef(null);
    setOpenAddSectionDialog(true);
  };

  const showEditSectionDialog = (sectionName: string) => {
    setSectionName(sectionName);
    setSettingDef(null);
    setOpenAddSectionDialog(true);
  };

  const updateSettingDef = (section: string, newSettingDef: any) => {
    settingDef ? onUpdateSettingDef(section, newSettingDef) : onAddSettingDef(section, newSettingDef);
    setOpenAddDialog(false);
  };

  const updateSection = (newSectionName: string) => {
    sectionName ? onUpdateSectionName(sectionName, newSectionName) : onAddNewSection(newSectionName);
    setOpenAddSectionDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Settings
        </Typography>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => showAddSectionDialog()}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none' }}
        >
          Add New Section
        </Button>
      </Stack>

      <Box sx={{ flex: 1, overflow: 'auto', pr: 0.5 }}>
        {settingDefs
          .filter((section) => !section.system)
          .map((section, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: '12px',
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {section.sectionName}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => showEditSectionDialog(section.sectionName)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Rename
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    color="error"
                    onClick={() => onDeleteSection(section.sectionName)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Delete Section
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => showAddDialog(section.sectionName)}
                    sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', boxShadow: 'none' }}
                  >
                    Add Setting
                  </Button>
                </Stack>
              </Stack>

              <Box sx={{ p: 1 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ borderRight: '1px solid', borderColor: 'divider', width: '23%', fontWeight: 700 }}
                        >
                          Name
                        </TableCell>
                        <TableCell
                          sx={{ borderRight: '1px solid', borderColor: 'divider', width: '23%', fontWeight: 700 }}
                        >
                          Label
                        </TableCell>
                        <TableCell
                          sx={{ borderRight: '1px solid', borderColor: 'divider', width: '20%', fontWeight: 700 }}
                        >
                          Type
                        </TableCell>
                        <TableCell
                          sx={{ borderRight: '1px solid', borderColor: 'divider', width: '15%', fontWeight: 700 }}
                        >
                          Required
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(section.items || []).map((item: any, sIdx: number) => (
                        <TableRow key={sIdx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.label}</TableCell>
                          <TableCell>{item.type}</TableCell>
                          <TableCell>{item.required ? 'Yes' : 'No'}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end">
                              <IconButton
                                size="small"
                                onClick={() => showEditDialog(section.sectionName, item)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => onDeleteSettingDef(section.sectionName, item)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          ))}
      </Box>

      {openAddDialog && (
        <SettingDefDialog
          sectionName={sectionName}
          settingDef={settingDef}
          settingNames={settingNames}
          open={openAddDialog}
          onCancel={() => setOpenAddDialog(false)}
          onSubmit={updateSettingDef}
        />
      )}

      {openAddSectionDialog && (
        <SettingDefSectionDialog
          sectionName={sectionName}
          open={openAddSectionDialog}
          onCancel={() => setOpenAddSectionDialog(false)}
          onSubmit={updateSection}
        />
      )}
    </Box>
  );
};

export default SettingDefs;
